import { type DocumentVersionSummary } from "~/api/types";
import { DocumentVersionStatus } from "~/config/types";
import {
  fileId,
  stripFileExtension,
  validateFile,
  type FileValidationError,
} from "~/helpers/file";

type DraftStatus = "idle" | "creating" | "failed" | "created";

export interface DraftRow {
  id: string;
  file: File;
  title: string;
  tierId: string | null;
  categoryId: string | null;
  status: DraftStatus;
}

export type RowPatch = Partial<
  Pick<DraftRow, "title" | "tierId" | "categoryId">
>;

export const isDraftRowValid = (row: DraftRow) =>
  row.title.trim() !== "" && row.tierId !== null && row.categoryId !== null;

export const hasExistingDuplicate = (
  row: DraftRow,
  documentVersions: DocumentVersionSummary[] | undefined,
) => {
  if (!documentVersions || row.categoryId === null) return false;
  const trimmed = row.title.trim();
  if (!trimmed) return false;
  const categoryId = Number(row.categoryId);
  return documentVersions.some(
    (v) =>
      v.document.category.id === categoryId &&
      v.status !== DocumentVersionStatus.archived &&
      v.title.toLowerCase() === trimmed.toLowerCase(),
  );
};

export type FileIntakeResult = {
  newRows: DraftRow[];
  rejected: { name: string; reason: FileValidationError }[];
  duplicateCount: number;
};

export const intakeFiles = (
  incoming: FileList | null,
  existing: DraftRow[],
): FileIntakeResult => {
  if (!incoming) {
    return { newRows: [], rejected: [], duplicateCount: 0 };
  }

  const seenFileIds = new Set(existing.map((r) => fileId(r.file)));
  const newRows: DraftRow[] = [];
  const rejected: FileIntakeResult["rejected"] = [];
  let duplicateCount = 0;

  for (const file of Array.from(incoming)) {
    const id = fileId(file);
    if (seenFileIds.has(id)) {
      duplicateCount++;
      continue;
    }
    const error = validateFile(file);
    if (error) {
      rejected.push({ name: file.name, reason: error });
      continue;
    }
    seenFileIds.add(id);
    newRows.push({
      id,
      file,
      title: stripFileExtension(file.name),
      tierId: null,
      categoryId: null,
      status: "idle",
    });
  }

  return { newRows, rejected, duplicateCount };
};

export const patchRows = (
  rows: DraftRow[],
  ids: Set<string>,
  patch: RowPatch,
): DraftRow[] =>
  rows.map((row) =>
    ids.has(row.id) && row.status !== "creating" ? { ...row, ...patch } : row,
  );

export const markRowsCreating = (
  rows: DraftRow[],
  ids: Set<string>,
): DraftRow[] =>
  rows.map((r) => (ids.has(r.id) ? { ...r, status: "creating" } : r));

export const markRowCreated = (rows: DraftRow[], id: string): DraftRow[] =>
  rows.map((r) => (r.id === id ? { ...r, status: "created" } : r));

// Failed rows move to the top to surface the failure; they stay selected
// so the same button retries them.
export const markFailedAndMoveToTop = (
  rows: DraftRow[],
  failedIds: Set<string>,
): DraftRow[] => {
  const failed = rows
    .filter((r) => failedIds.has(r.id))
    .map<DraftRow>((r) => ({ ...r, status: "failed" }));
  const rest = rows.filter((r) => !failedIds.has(r.id));
  return [...failed, ...rest];
};
